import type {
  IrrigationRepairRow,
  ProposalBlock,
  ProposalDocumentDto,
} from '../dto/proposal-document.dto.js'
import {
  UNPRICEABLE_REASON_MESSAGES,
  unpriceableReason,
  type UnpriceableReason,
} from './irrigation-pricing.js'
import { parseMoney } from './money.js'
import {
  EMPTY_SELECTION,
  irrigationScheduleBlocks,
  priceSelection,
} from './proposal-selection.js'

/**
 * Why a document must not reach a client yet, or null when it can.
 *
 * An empty proposal is worse than it sounds, because it still looks like one:
 * the cover, the summary, the standard sections and the signature block all
 * render, and the only tell is a row of zeros. A client who receives it has
 * been quoted nothing by a business that appears to have quoted something, and
 * since sending freezes the document, it cannot be corrected afterwards, only
 * superseded by another version.
 *
 * Judged from the document's own summary rather than from the database, so the
 * rule is about what the client would actually see, and so a frozen snapshot
 * and a live resolve are held to the same standard.
 *
 * The messages say what to do, not just what is wrong: they reach the person
 * who pressed Send, who is looking at a proposal screen, not a log.
 */
export function describeUnsendable(
  document: ProposalDocumentDto,
): string | null {
  const summary = document.blocks.find(
    (block): block is Extract<ProposalBlock, { type: 'summary' }> =>
      block.type === 'summary',
  )

  // Every built document has one, so its absence means something is wrong with
  // the document itself, and "cannot tell" is not a reason to let it through.
  if (summary === undefined) {
    return 'This proposal could not be checked, so it was not sent. Open the preview and try again.'
  }

  if (summary.siteCount === 0) {
    return 'This proposal has no sites attached, so there is nothing to send. Pick the site it covers under "Sites covered", then send it.'
  }

  if (summary.treeCount === 0 && (summary.irrigationRepairCount ?? 0) === 0) {
    if (document.includesIrrigation) {
      const what = document.includesTrees
        ? 'trees or open irrigation repairs'
        : 'open irrigation repairs'
      const finding =
        summary.siteCount === 1
          ? `The site on this proposal has no ${what} recorded`
          : `None of the sites on this proposal have any ${what} recorded`
      return `${finding}, so there is nothing to quote. Survey the site first, then send it.`
    }
    const finding =
      summary.siteCount === 1
        ? 'The site on this proposal has no trees recorded'
        : 'None of the sites on this proposal have any trees recorded'
    return `${finding}, so there is nothing to quote. Survey the trees first, then send it.`
  }

  /*
   * A total with a tree missing from it is not a total.
   *
   * This used to go out with a sentence saying the figure was a floor. That
   * is honest and still the wrong thing to send: the number beside the
   * signature is the one that gets held to, a board compares it against two
   * finished bids, and "the final figure will be higher" is the sentence a
   * competitor's bid does not have. The fix is a time estimate, which can be
   * zero, so the refusal says which trees and what to do.
   */
  const unestimated = document.blocks.flatMap((block) =>
    block.type === 'treeSchedule'
      ? block.rows
          .filter((row) => row.estimatedLaborMinutes === null)
          .map((row) => ({ site: block.siteName, label: row.label }))
      : [],
  )
  if (unestimated.length > 0) {
    const sites = new Set(unestimated.map((tree) => tree.site))
    const names = unestimated
      .slice(0, 3)
      .map((tree) =>
        sites.size > 1 ? `${tree.label} (${tree.site})` : tree.label,
      )
      .join(', ')
    const more =
      unestimated.length > 3
        ? ` and ${String(unestimated.length - 3)} more`
        : ''
    if (unestimated.length === 1) {
      return `Tree ${names} has no time estimate yet, so the total would be short. Give it an estimate, even zero, or mark it “No pruning recommended”, then send it.`
    }
    return `${String(unestimated.length)} trees have no time estimate yet (${names}${more}), so the total would be short. Give each one an estimate, even zero, or mark it “No pruning recommended”, then send it.`
  }

  return describeUnpriceableRepairs(document) ?? describeUnpriced(document)
}

/**
 * A proposal that charges for nothing, as a reason not to send.
 *
 * Every tree on it may be answered without a price, "No pruning recommended"
 * above all. That is worth telling a client, but only beside something they
 * are being asked to pay for: a document whose total is zero asks a board to
 * approve nothing, and still looks like a bid. So at least one tree or
 * irrigation repair has to cost something, as the client would first see it.
 * Work for a later season does not count; nobody approves it here.
 */
function describeUnpriced(document: ProposalDocumentDto): string | null {
  const total = parseMoney(
    priceSelection(document, EMPTY_SELECTION).totalAmount,
  )
  if (total !== null && total > 0) return null

  let rateIsSet = true
  let hasMinutes = false
  for (const block of document.blocks) {
    if (block.type === 'pricing') rateIsSet = block.ratePerHour !== null
    if (block.type === 'treeSchedule') {
      hasMinutes ||= block.rows.some(
        (row) =>
          (row.estimatedLaborMinutes ?? 0) > 0 ||
          (row.removal?.minutes ?? 0) > 0,
      )
    }
  }
  if (!rateIsSet && hasMinutes) {
    return 'No tree labor rate is set, so the time on this proposal has no price. Set one in Settings, then send it.'
  }
  return 'Nothing on this proposal has a price, so there is nothing to approve. Price at least one tree or repair, then send it.'
}

/** "Bad nozzle on Clubhouse 4" */
function repairName(repair: IrrigationRepairRow): string {
  return `${repair.kind} on ${repair.subject}`
}

/**
 * The irrigation repairs that cannot be priced, as a reason not to send.
 *
 * The same rule as an unestimated tree: a total with a repair missing from it
 * is not a total. A missing labor service is a setting rather than a survey
 * problem, so it gets its own sentence pointing at Settings.
 */
function describeUnpriceableRepairs(
  document: ProposalDocumentDto,
): string | null {
  const unpriceable: {
    repair: IrrigationRepairRow
    reason: UnpriceableReason
  }[] = []
  for (const block of irrigationScheduleBlocks(document)) {
    for (const repair of block.repairs) {
      const reason = unpriceableReason(repair, block.rateCard)
      if (reason !== null) unpriceable.push({ repair, reason })
    }
  }
  if (unpriceable.length === 0) return null

  if (unpriceable.some((entry) => entry.reason === 'noLabor')) {
    return "No labor service is chosen for this client's type, so the irrigation repairs cannot be priced. Choose one in Settings → Irrigation, then send it."
  }
  const [first] = unpriceable
  if (unpriceable.length === 1 && first !== undefined) {
    return `${repairName(first.repair)} ${UNPRICEABLE_REASON_MESSAGES[first.reason]}, so it cannot be priced. Fix it in the irrigation survey, then send it.`
  }
  const names = unpriceable
    .slice(0, 3)
    .map((entry) => repairName(entry.repair))
    .join(', ')
  const more =
    unpriceable.length > 3 ? ` and ${String(unpriceable.length - 3)} more` : ''
  return `${String(unpriceable.length)} irrigation repairs cannot be priced yet (${names}${more}). Fix them in the irrigation survey, then send it.`
}
